import React from 'react';
import regexifyString from "regexify-string";
import validator from "validator";
import uniqid from 'uniqid';
import linkifyHtml from 'linkifyjs/html';
import ReactHtmlParser, {convertNodeToElement} from 'react-html-parser';
import { startsWith, isString } from 'lodash';
import sanitizeHtml from 'sanitize-html';
import Mark from 'mark.js/src/vanilla';
import {EmojiBySlug} from './emoji';
import stopWords from '../../content/stopwords';

const linkRx = /<a *href="([^'"]+)[\s\S]*?>([\s\S]*?)<\/a>/;

const buildLinks = (text) => {
  if (linkRx.test(text)) {
    return regexifyString({
      pattern: linkRx,
      decorator: (match, i) => {
        const allMatches = match.match(linkRx);
        return <a
          href={allMatches[1]}
          key={uniqid()}
          style={{ whiteSpace: 'pre-line' }}
          target="_blank"
          rel="noopener noreferrer"
        >
          {allMatches[2] || allMatches[1]}
        </a>
      },
      input: text,
    })
  }

  return regexifyString({
    pattern: /<(.*?)>/g,
    decorator: (match, i) => {
      let slimMatch = match.replace('<', '').replace('>', '');
      // add mailto link
      if (validator.isEmail(slimMatch)) {
        return <a
          key={uniqid()}
          href={`mailto:${slimMatch}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {slimMatch}
        </a>
      }
      if (validator.isURL(slimMatch)) {
        // link to resource
        return <a
          key={uniqid()}
          href={slimMatch}
          target="_blank"
          rel="noopener noreferrer"
        >
          {slimMatch}
        </a>
      } else if (slimMatch.indexOf('|') > -1) {
        const url = slimMatch.split('|')[0];
        if (validator.isURL(url)) {
          // link to resource
          return <a
            key={uniqid()}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {url}
          </a>
        }
      }
      // no link, just decorated text
      return (
        <span key={`${match}-${i}`} style={{color: "#007bff" }}>{slimMatch}</span>
      )
    },
    input: text,
  });
}

const highlightText = (text, term) => {
  const searchTerm = term.trim();
  if (!searchTerm) {
    return text;
  }
  const pattern = new RegExp(`\\b${searchTerm.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')}\\b`, 'gi');

  return regexifyString({
    pattern: new RegExp(pattern, 'gi'),
    decorator: (match, i) => {
      return <span key={uniqid()} style={{color: '#EF1C62', fontWeight: 600}}>{match}</span>
    },
    input: text,
  })
}

const highlightTerm = (text, term) => {
  const searchTerm = term.trim();
  if (!searchTerm) {
    return text;
  }
  const pattern = new RegExp(`\\b${searchTerm.replace(/[-[\]{}()*+?.,\\^$|#\s]/gi, '\\$&')}\\b`, 'gi');

  return text.replace(pattern, (match) => {
    return `<span style="font-weight: 600; color: #EF1C62">${match}</span>`
  })
}

export const highlightAndLinkString = (text, highlights) => {
  if (!text) {
    return '';
  }
  let linksAdded = markLinks(
    text
      .replace('<u>', '')
      .replace('</u>', '')
      .replace('>', '&gt')
      .replace('<', '&lt')
  );
  /*const parts = linksAdded.split(/<\/{0,1}a[^>]*!/);
  parts.forEach((p) => {
    linksAdded = linksAdded.replace(p, highlightQuery(p, highlights))
  });*/
  return linksAdded;
}

export const highlightAndLink = (text, highlights) => {
  const linksAdded = highlightAndLinkString(text, highlights);
  return ReactHtmlParser(linksAdded);
}

export const highlightAndLinkText = (text, highlights) => {
  if (!text) {
    return '';
  }
  let linksAdded = markLinks(text.replace('<u>', '').replace('</u>', ''));
  /*const parts = linksAdded.split(/<\/{0,1}a[^>]*!/);
  parts.forEach((p) => {
    linksAdded = linksAdded.replace(p, highlightQuery(p, highlights))
  });*/
  return linksAdded;
}

export const markLinks = (text) => {
  return linkifyHtml(text, {
    ignoreTags: ['script', 'style'],
    truncate: 30,
    target: '_blank',
  });
}

export const parserTransformLinks = (node, index) => {
  if (node.type === 'tag' && node.name === 'a') {
    node.attribs.target = '_blank';
    return convertNodeToElement(node, index, parserTransformLinks);
  }
}

export const beautifyLinks = (text) => {
  if (typeof text === 'string') {
    return buildLinks(text);
  }
  return text.flat().map(part => buildLinks(part));
}

export const highlightSearchTerm = (text, words) => {
  if (!words || !words.length || !text) {
    return text;
  }
  return words.reduce((current, t) => {
    if (typeof current === 'string') {
      return highlightText(current, t);
    }
    return current.flat().map(part => highlightText(part, t));
  }, text)
}

export const highlightQuery = (text, words) => {
  if (!words || !words.length || !text) {
    return text;
  }
  return words.reduce((current, t) => {
    if (typeof current === 'string') {
      return highlightTerm(current, t);
    }

    return current.flat().map(part => {
      return highlightTerm(part, t)
    });
  }, text)
}

export const cleanForwardedMail = (text) => {
  if (!text) {
    return '';
  }
  let snippet = text;
  if (validator.contains(snippet, '- Forwarded message -')) {
    // find first Subject index
    const idx = snippet.indexOf('Subject');
    // keep only message from Subject onwards
    snippet = snippet.slice(idx);
  }
  return snippet;
}

export const formatMailReply = (text) => {
  return regexifyString({
    pattern: /On(.*?)wrote:/g,
    decorator: (match, i) => {
      return <p>{match}</p>
    },
    input: text,
  })
}

export const cleanTeamsMessage = (text) => {
  let message = text.replace(/<div>/g, '').replace(/<\/div>/g,'');
  return regexifyString({
    pattern: /(?:<at.*?>)(.*?)<\/at>/gm,
    decorator: (match, i) => {
      const slimMatch = match.replace(/<at.*?>/, '').replace(/<\/at>/, '');
      return <span style={{color: "#007bff" }}>{slimMatch.replace(/&nbsp;/g, ' ')}</span>
    },
    input: message,
  });
}

export const replaceSlackUsername = (text, users) => {
  if (!users || !users.length) {
    return text;
  }
  if (!text) {
    return '';
  }
  let testTxt = text;
  const regex = /<@(.*?)>/g;
  const matches = testTxt.match(regex);
  if (!matches) {
    return text;
  }

  matches.forEach(m => {
    const slimM = m.replace('<@', '').replace('>', '');
    const userName = users.find(u => u.user_id === slimM)?.real_name;
    if (userName) {
      testTxt = testTxt.replace(m, userName);
    } else {
      testTxt = testTxt.replace(m, (sm) => {
        const trimmedMatch = sm.replace('<@', '').replace('>', '');
        return `<span key="${uniqid()}" style="color:#007bff">${trimmedMatch}</span>`
      });
    }
  });
  return testTxt;
}

export const cleanEmails = (text) => {
  let res = cleanForwardedMail(text);
  res = formatMailReply(res);
  return res;
}

export const isEmptyHtml = (dom) => {
  if (!dom || !dom.body) {
    return true;
  }
  return !Array.from(dom.body.childNodes).some(node => !!node.textContent.trim());
}

export const extractSnippet = (text, l) => {
  if (!text) {
    return '';
  }
  const limit = l || 220;
  const htmlPattern = /(<[^>]*>)/g;
  const noHTMLText = sanitizeHtml(text, {allowedTags: []});

  // no truncating needed
  if (noHTMLText.length <= limit) {
    return text;
  }

  const parts = text.split(htmlPattern).filter(Boolean);
  let processed = [], i = 0, count = 0;

  while (i < parts.length) {
    const part = parts[i];
    //is not html part
    if (!startsWith(part, '<')) {
      if (count >= limit) {
        break;
      }

      if ((count + part.length) > limit) {
        processed.push(`${part.substring(0, (limit - count) - 1)}...`)
      } else {
        processed.push(part);
      }
      count += part.length;
    } else {
      processed.push(part);
    }
    i += 1;
  }

  return processed.join('');
}

export const formatHtmlInput = (htmlInput, highlights) => {
  if (!htmlInput) {
    return '';
  }
  let result = htmlInput
    // replace multiple line breaks with just one
    /*.replace(/(\r\n|\r|\n){2,}/g, '$1')
    .replace(/\n\s*\n/g, '\n')
    // find and replace special chars with UTF-8 compliant unicode chars
    .replace(
      /(?:\\x[\da-fA-F]{2})+/g,
      (m) => decodeURIComponent(m.replace(/\\x/g, '%'))
    );*/
  const dom = new DOMParser().parseFromString(result, 'text/html');
  if (isEmptyHtml(dom)) {
    return '';
  }
  if (result.indexOf('<body') > -1) {
    const bodyStyle = dom.body.getAttribute('style');
    const bodyContent = dom.body.innerHTML;
    result = `<div style="${bodyStyle}; max-width: 100%">${bodyContent}</div>`;
  } else {
    //result = highlightAndLinkString(result);
  }

  result = markLinks(result);

  //const parts = result.split(/<\/{0,1}(.*?)>*/).filter(p => !!p.trim());
  /*parts.forEach((p) => {
    result = result.replace(p, highlightQuery(p, highlights))
  });*/
  return result;
}

export const formatEmojis = (txt) => {
  const regex = /:([^:\s]*(?:::[^:\s]*)*):/g;
  return txt.replace(regex, (match, p1) => {
    if (EmojiBySlug[p1]) {
      return EmojiBySlug[p1].char;
    }
    return match;
  })
}

export const removeStopWords = (text) => {
  const words = text.split(' ');
  const newWords = words.filter((w) =>
    stopWords.indexOf(w.split('.').join('').toLowerCase()) === -1
  )
  return newWords.join(' ');
}

export const formatJiraText = (txt) => {
  if (!txt || typeof txt !== 'string') {
    return txt;
  }
  let text = txt.replace(/\[([^[]*)\]/gm, (match, p1) => {
    const firstChar = p1.charAt(0);
    // accountID
    if (firstChar === '~') {
      return '';
    }
    // video
    if (firstChar === '^') {
      return ''
    }
    // link
    if (p1.indexOf('|') > -1) {
      const parts = p1.split('|');
      return `<a href="${parts[1]}" target="jira">${parts[0]} </a>`;
    }
    return p1;
  });
  // numbered list
  if (text.indexOf('#') > -1) {
    const parts = text.split('#');
    text = parts.map((p, i) => {
      if ((!i && txt.charAt(0) !== '#') || i === (p.length - 1) || !p) {
        return p;
      }
      if (p.charAt(0) !== ' ' || parts[i - 1].slice(-2) === '\n') {
        return `#${p}`;
      }
      return `\n${i}. ${p}\n`
    }).join('');
  }
  // bullet list
  if (text.indexOf('*') > -1) {
    const parts = text.split('*');
    text = parts.map((p, i) => {
      if ((!i && text.charAt(0) !== '*') || i === (p.length - 1) || !p) {
        return p;
      }
      if (p.charAt(0) !== ' ' || parts[i - 1].slice(-2) === '\n') {
        return `*${p}`;
      }
      return `\n\u2022 ${p}\n`
    }).join('');
  }
  // image reference
  text = text.replace(/!.*!/gm, '')
  // backslashes escaping punctuation
  text = text.replace(/\\/g, '');
  return text;
}

export const formatJiraComments = (comments) => {
  if (!comments || !comments.length) {
    return null;
  }
  const formatted = comments.map((c) => {
    let txt = c.body;
    txt = formatJiraText(txt || '');
    return {
      ...c,
      body: ReactHtmlParser(txt),
    };
  });
  return formatted;
}

export const getHtmlBodyContent = (html) => {
  const dom = new DOMParser().parseFromString(html, 'text/html');
  return {
    style: dom.body.getAttribute('style'),
    content: dom.body.innerHTML,
    text: getHtmlInnerText(dom.body),
    body: dom.body,
  }
}

export const getHtmlInnerText = (html) => {
  function getTextLoop(element) {
    const texts = [];
    Array.from(element.childNodes).forEach(node => {
      if (node.nodeType === 3 && !node.childNodes.length) {
        const txt = node.textContent.replace(/<!--[\s\S]*?-->/g).trim();
        if (txt && txt !== 'undefined') {
          texts.push(txt);
        }
      } else {
        texts.push(...getTextLoop(node));
      }
    });
    return texts;
  }
  return getTextLoop(html).join(' ');
}

export const markQueryWords = (str, words) => {
  if (!isString(str)) {
    console.error('string must be provided for marking');
    return;
  }
  // cleanup previous marks
  let marked = str.replace(/(<mark class="highlight">|<\/mark>)/gim, '');
  words.forEach(word => {
    if (!word || !isString(word)) {
      return;
    }
    //  const pattern = new RegExp(`\\b${searchTerm.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')}\\b`, 'gi');
    const rx = new RegExp(word, 'gi');
    marked.replace(rx, '<mark class="highlight">$&</mark>');
  });
  return marked;
}

export const markDomContent = (dom, words) => {
  const instance = new Mark(dom);
  const options = {
    ignorePunctuation: ":;.,-–—‒_(){}[]!'\"+=".split(""),
    accuracy: 'exactly',
  }
  const marked = instance.mark(words, options);
  console.log(marked);
  return marked;
}